/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addServicePackage,
  addServicePackageRootItem,
  addServicePackageSubItem,
  editServicePackage,
  editServicePackageRootItem,
  editServicePackageSubItem,
  getServicePackageItems,
  getServicePackages,
  removeServicePackage,
  removeServicePackageItem,
  slideDownServicePackageItem,
  slideUpServicePackageItem,
  validateServicePackage,
} from "@/requests/administration/servicesAndAttributes/administrationServicePackages";
import { ServicePackage } from "@/models/api/administration/servicesAndAttributes/ServicePackage";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { ServicePackageItem } from "@/models/api/administration/servicesAndAttributes/ServicePackageItem";
import { ServicePackageRootItem } from "@/models/api/administration/servicesAndAttributes/ServicePackageRootItem";
import { ServicePackageSubItem } from "@/models/api/administration/servicesAndAttributes/ServicePackageSubItem";

export interface AdministrationServicePackagesStateInterface {
  servicePackages: Array<ServicePackage>;
  selectedPackageId: null | string;
  packageItems: Array<ServicePackageItem>;
  isEditDialog: boolean;
  dialogServicePackageId: null | string;
  dialogServicePackageName: string;
  isRootItemDialog: boolean;
  rootItemId: null | string;
  rootItem: ServicePackageRootItem;
  rootItemServiceTypeName: null | string;
  isSubItemDialog: boolean;
  subItemId: null | string;
  subItem: ServicePackageSubItem;
}

export const AdministrationServicePackagesState: AdministrationServicePackagesStateInterface = {
  servicePackages: [],
  packageItems: [],
  selectedPackageId: null,
  isEditDialog: false,
  dialogServicePackageId: null,
  dialogServicePackageName: "",
  isRootItemDialog: false,
  rootItemId: null,
  rootItem: new ServicePackageRootItem(),
  rootItemServiceTypeName: null,
  isSubItemDialog: false,
  subItemId: null,
  subItem: new ServicePackageSubItem(null),
};

const AdministrationServicePackagesModule = defineModule({
  state: AdministrationServicePackagesState as AdministrationServicePackagesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setServicePackages(state, param: Array<ServicePackage>) {
      state.servicePackages = param;
    },
    setSelectedPackageId(state, param: null | string) {
      state.selectedPackageId = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setDialogServicePackageId(state, param: null | string) {
      state.dialogServicePackageId = param;
    },
    setDialogServicePackageName(state, param: string) {
      state.dialogServicePackageName = param;
    },
    setPackageItems(state, param: Array<ServicePackageItem>) {
      state.packageItems = param;
    },
    setIsRootItemDialog(state, param: boolean) {
      state.isRootItemDialog = param;
    },
    setRootItem(state, param: ServicePackageRootItem) {
      state.rootItem = param;
    },
    setRootItemServiceTypeName(state, param: null | string) {
      state.rootItemServiceTypeName = param;
    },
    setRootItemId(state, param: null | string) {
      state.rootItemId = param;
    },
    setIsSubItemDialog(state, param: boolean) {
      state.isSubItemDialog = param;
    },
    setSubItemId(state, param: null | string) {
      state.subItemId = param;
    },
    setSubItem(state, param: ServicePackageSubItem) {
      state.subItem = param;
    },
  },
  actions: {
    async downloadPackages(context, skipSetDefault: boolean | undefined) {
      const { commit } = _moduleActionContext(context);
      const packages = await getServicePackages();
      commit.setServicePackages(packages);
      if (!skipSetDefault && packages.length > 0) {
        commit.setSelectedPackageId(packages[0].id);
      }
    },
    async downloadPackageItems(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedPackageId !== null) {
        const packageItems = await getServicePackageItems(state.selectedPackageId);
        commit.setPackageItems(packageItems);
      }
    },
    async saveServicePackage(context) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      let id = state.dialogServicePackageId;
      if (state.dialogServicePackageId !== null) {
        await editServicePackage(state.dialogServicePackageName, state.dialogServicePackageId);
      } else {
        id = await addServicePackage(state.dialogServicePackageName);
      }
      commit.setIsEditDialog(false);
      await dispatch.downloadPackages(true);
      commit.setSelectedPackageId(id);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async openAddServicePackageDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setDialogServicePackageId(null);
      commit.setDialogServicePackageName("");
      commit.setIsEditDialog(true);
    },
    async openEditServicePackageDialog(context, servicePackage: ServicePackage) {
      const { commit } = _moduleActionContext(context);
      commit.setDialogServicePackageId(servicePackage.id);
      commit.setDialogServicePackageName(servicePackage.name);
      commit.setIsEditDialog(true);
    },
    async validateServicePackage(context, servicePackageId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      await validateServicePackage(servicePackageId);
      rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("packageValid"))));
    },
    async removeServicePackage(context, servicePackageId: string) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeServicePackage(servicePackageId);
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setSelectedPackageId(null);
        await dispatch.downloadPackages();
      }
    },
    async openAddRootItemDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setRootItem(new ServicePackageRootItem());
      commit.setRootItemServiceTypeName(null);
      commit.setRootItemId(null);
      commit.setIsRootItemDialog(true);
    },
    async openEditRootItemDialog(context, servicePackageItem: ServicePackageItem) {
      const { commit } = _moduleActionContext(context);
      commit.setRootItem(ServicePackageRootItem.fromServicePackageItem(servicePackageItem));
      commit.setRootItemServiceTypeName(servicePackageItem.name);
      commit.setRootItemId(servicePackageItem.id);
      commit.setIsRootItemDialog(true);
    },
    async saveRootItem(context) {
      const { commit, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPackageId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        if (state.rootItemId === null) {
          await addServicePackageRootItem(state.selectedPackageId, state.rootItem);
        } else {
          await editServicePackageRootItem(state.selectedPackageId, state.rootItem, state.rootItemId);
        }
        // noinspection ES6MissingAwait
        dispatch.downloadPackageItems();
        commit.setIsRootItemDialog(false);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async openNewSubItemDialog(context, servicePackageRootItem: ServicePackageItem) {
      const { commit, rootDispatch, rootState } = _moduleActionContext(context);
      const commissionType = await rootDispatch.serviceTypes.getCommissionServiceType(
        servicePackageRootItem.serviceTypeId
      );
      const subItem = new ServicePackageSubItem(servicePackageRootItem.id);
      subItem.serviceTypeId = commissionType === null ? servicePackageRootItem.serviceTypeId : commissionType;
      subItem.name = rootState.serviceTypes.commissions?.find((v) => v.value === subItem.serviceTypeId)?.text as string;
      commit.setSubItem(subItem);
      commit.setSubItemId(null);
      commit.setIsSubItemDialog(true);
    },
    async openEditSubItemDialog(context, servicePackageSubItem: ServicePackageItem) {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      const parentServiceTypeId = state.packageItems.find(
        (v) => v.id === servicePackageSubItem.parentServicePackageItemId
      )?.serviceTypeId;
      await rootDispatch.serviceTypes.getCommissionServiceType(parentServiceTypeId as number);
      commit.setSubItemId(servicePackageSubItem.id);
      const subItem = ServicePackageSubItem.fromServicePackageItem(servicePackageSubItem);
      commit.setSubItem(subItem);
      commit.setIsSubItemDialog(true);
    },
    async saveSubItem(context) {
      const { commit, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPackageId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        if (state.subItemId === null) {
          await addServicePackageSubItem(state.selectedPackageId, state.subItem);
        } else {
          await editServicePackageSubItem(state.selectedPackageId, state.subItem, state.subItemId);
        }
        // noinspection ES6MissingAwait
        dispatch.downloadPackageItems();
        commit.setIsSubItemDialog(false);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async slideUpServicePackageItem(context, servicePackageItemId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPackageId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await slideUpServicePackageItem(state.selectedPackageId, servicePackageItemId);
        // noinspection ES6MissingAwait
        dispatch.downloadPackageItems();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async slideDownServicePackageItem(context, servicePackageItemId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPackageId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await slideDownServicePackageItem(state.selectedPackageId, servicePackageItemId);
        // noinspection ES6MissingAwait
        dispatch.downloadPackageItems();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeServicePackageItem(context, servicePackageItemId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedPackageId !== null) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) {
          await removeServicePackageItem(state.selectedPackageId, servicePackageItemId);
          // noinspection ES6MissingAwait
          dispatch.downloadPackageItems();
          rootDispatch.popup.addOperationSuccessfulSnackbar();
        }
      }
    },
  },
});

export default AdministrationServicePackagesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, AdministrationServicePackagesModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, AdministrationServicePackagesModule);
